import type { PopDevToolsLevel, PopDevToolsRecordInput } from './types';

export interface PopDevToolsMeteringResult {
  metered: boolean;
  summaries: PopDevToolsRecordInput[];
  nextFlushAt?: number;
}

interface MeteredWindow {
  streamKey: string;
  channel: NonNullable<PopDevToolsRecordInput['channel']>;
  source: NonNullable<PopDevToolsRecordInput['source']>;
  windowType?: string;
  via?: string;
  sessionId?: string;
  peerId?: string;
  trackId?: string;
  firstTs: number;
  lastTs: number;
  count: number;
  level: PopDevToolsLevel;
  firstMessage: string;
  lastMessage: string;
  samples: unknown[];
}

const levelRank: Record<PopDevToolsLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

export class PopDevToolsMetering {
  private readonly enabledStreams = new Map<string, boolean>();
  private readonly windows = new Map<string, MeteredWindow>();

  constructor(readonly windowMs = 2000) {}

  setMetering(streamKey: string, enabled: boolean) {
    if (enabled) {
      this.enabledStreams.set(streamKey, true);
      return;
    }

    this.enabledStreams.set(streamKey, false);
  }

  isMetered(streamKey: string) {
    return this.enabledStreams.get(streamKey) === true;
  }

  getMeteredStreams() {
    return new Map(this.enabledStreams);
  }

  record(input: PopDevToolsRecordInput, now: number): PopDevToolsMeteringResult {
    if (!input.streamKey || !this.isMetered(input.streamKey)) {
      return { metered: false, summaries: [input] };
    }

    const summaries: PopDevToolsRecordInput[] = [];
    const activeWindow = this.windows.get(input.streamKey);
    if (activeWindow && now - activeWindow.firstTs >= this.windowMs) {
      summaries.push(this.toSummary(activeWindow));
      this.windows.delete(input.streamKey);
    }

    this.addToWindow(input, input.streamKey, now);
    const nextFlushAt = this.windows.get(input.streamKey)?.firstTs;
    return {
      metered: true,
      summaries,
      nextFlushAt: nextFlushAt === undefined ? undefined : nextFlushAt + this.windowMs,
    };
  }

  flushDue(now: number) {
    const summaries: PopDevToolsRecordInput[] = [];

    for (const [streamKey, window] of this.windows) {
      if (now - window.firstTs >= this.windowMs) {
        summaries.push(this.toSummary(window));
        this.windows.delete(streamKey);
      }
    }

    return summaries;
  }

  flushAll(_now: number) {
    const summaries: PopDevToolsRecordInput[] = [];

    for (const [streamKey, window] of this.windows) {
      summaries.push(this.toSummary(window));
      this.windows.delete(streamKey);
    }

    return summaries;
  }

  getNextFlushAt() {
    let nextFlushAt: number | undefined;

    for (const window of this.windows.values()) {
      const candidate = window.firstTs + this.windowMs;
      if (nextFlushAt === undefined || candidate < nextFlushAt) nextFlushAt = candidate;
    }

    return nextFlushAt;
  }

  clear() {
    this.windows.clear();
  }

  private addToWindow(input: PopDevToolsRecordInput, streamKey: string, now: number) {
    const ts = input.ts ?? now;
    const existing = this.windows.get(streamKey);
    if (!existing) {
      this.windows.set(streamKey, {
        streamKey,
        channel: input.channel ?? 'console',
        source: input.source ?? 'renderer',
        windowType: input.windowType,
        via: input.via,
        sessionId: input.sessionId,
        peerId: input.peerId,
        trackId: input.trackId,
        firstTs: ts,
        lastTs: ts,
        count: 1,
        level: input.level ?? 'info',
        firstMessage: input.message,
        lastMessage: input.message,
        samples: input.data === undefined ? [] : [input.data],
      });
      return;
    }

    existing.lastTs = ts;
    existing.count += 1;
    existing.lastMessage = input.message;
    existing.windowType = input.windowType ?? existing.windowType;
    existing.via = input.via ?? existing.via;
    existing.sessionId = input.sessionId ?? existing.sessionId;
    existing.peerId = input.peerId ?? existing.peerId;
    existing.trackId = input.trackId ?? existing.trackId;
    if (levelRank[input.level ?? 'info'] > levelRank[existing.level]) existing.level = input.level ?? 'info';

    if (input.data !== undefined) {
      existing.samples.push(input.data);
      if (existing.samples.length > 3) existing.samples.shift();
    }
  }

  private toSummary(window: MeteredWindow): PopDevToolsRecordInput {
    return {
      ts: window.lastTs,
      source: window.source,
      windowType: window.windowType,
      channel: window.channel,
      level: window.level,
      message: window.count > 1 ? `${window.firstMessage} x${window.count}` : window.firstMessage,
      via: window.via,
      data: {
        streamKey: window.streamKey,
        count: window.count,
        firstTs: window.firstTs,
        lastTs: window.lastTs,
        durationMs: Math.max(0, window.lastTs - window.firstTs),
        firstMessage: window.firstMessage,
        lastMessage: window.lastMessage,
        samples: window.samples,
      },
      sessionId: window.sessionId,
      peerId: window.peerId,
      trackId: window.trackId,
      count: window.count,
    };
  }
}
